import axiosInstance from './axiosInstance';

/**
 * Fetches per-day solved counts for a student and returns a
 * continuous array covering every date in the requested range.
 *
 * @param {string} userId    - User._id  OR  StudentProfile._id
 * @param {string} startDate - 'YYYY-MM-DD'
 * @param {string} endDate   - 'YYYY-MM-DD'
 *
 * @returns {Promise<Array<{ date: string, count: number }>>}
 *   One entry per calendar day, ordered ascending.
 *   Days with no submissions have count = 0.
 *
 * @example
 * const data = await getSubmissionHeatmap('abc123', '2026-01-01', '2026-03-14');
 * // [{ date: '2026-01-01', count: 3 }, { date: '2026-01-02', count: 0 }, ...]
 */
export async function getSubmissionHeatmap(userId, startDate, endDate) {
  // ── 1. Validate inputs before hitting the network ─────────────────────────
  if (!userId)    throw new Error('userId is required.');
  if (!startDate) throw new Error('startDate is required.');
  if (!endDate)   throw new Error('endDate is required.');

  const startMs = Date.parse(startDate);
  const endMs   = Date.parse(endDate);

  if (isNaN(startMs)) throw new Error('startDate is not a valid date.');
  if (isNaN(endMs))   throw new Error('endDate is not a valid date.');
  if (startMs > endMs) throw new Error('startDate must be on or before endDate.');

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  if ((endMs - startMs) / MS_PER_DAY > 365)
    throw new Error('Date range must not exceed 365 days.');

  // ── 2. Fetch from backend ─────────────────────────────────────────────────
  const { data } = await axiosInstance.get(
    `/students/${userId}/leetcode/progress`,
    { params: { startDate, endDate } }
  );

  // ── 3. Build lookup dictionary from backend response ──────────────────────
  //   { "2026-01-01": 3, "2026-01-02": 0, ... }
  //   The backend already fills gaps, but we rebuild the dict here so the
  //   service can work even if the backend omits zero-count days in the future.
  /** @type {Record<string, number>} */
  const countByDate = {};
  for (const entry of data.dailySolvedCounts || []) {
    countByDate[entry.date] = entry.count;
  }

  // ── 4. Generate all dates in the range & merge with counts ────────────────
  //   Guarantees continuity regardless of what the server returned.
  /** @type {Array<{ date: string, count: number }>} */
  const heatmap = [];

  const cursor = new Date(`${startDate}T00:00:00Z`);
  const endDay = new Date(`${endDate}T00:00:00Z`);

  while (cursor <= endDay) {
    const dateStr = cursor.toISOString().slice(0, 10); // 'YYYY-MM-DD'
    heatmap.push({ date: dateStr, count: countByDate[dateStr] ?? 0 });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return heatmap;
}

/**
 * Fetch weekly LeetCode activity trends.
 *
 * @param {{ weeks?: number, labId?: string }} [params]
 * @returns {Promise<Array<{ week: string, activeStudents: number, totalSubmissions: number }>>}
 */
export async function getWeeklyLeetCodeActivityTrends({ weeks = 12, labId, batchId } = {}) {
  const parsedWeeks = Number(weeks);
  if (!Number.isInteger(parsedWeeks) || parsedWeeks <= 0) {
    throw new Error('weeks must be a positive integer.');
  }
  if (!batchId) {
    throw new Error('batchId is required.');
  }

  const query = { weeks: parsedWeeks, batchId };
  if (labId) query.labId = labId;

  const { data } = await axiosInstance.get('/analytics/leetcode/weekly', {
    params: query,
  });

  return Array.isArray(data) ? data : [];
}
